import React, { useRef } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import {
    useForm,
    SubmitHandler,
    SubmitErrorHandler,
    Controller,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ReCAPTCHA from "react-google-recaptcha";
import Select from "react-select";
import * as yup from "yup";

import "../../assets/css/wibb/pages/contact.scss";

import { IContactFormData } from "../../types/forms";
import { IAPIResponse, IAgeLevel } from "../../types/api";
import { getAgeLevels, submitContact } from "../../lib/api";
import { useLoaderData } from "react-router";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { alertActions } from "../../features/alert/alertSlice";
import { displayFormAlert } from "../../lib/utils";
import { store } from "../..";
import { tournamentManagementActions } from "../../features/tourneymgmt/tournamentManagementSlice";

export const loader = async () => {
    const tournaments = store.getState().tournamentmanagement.masterTournaments;

    const tournamentsPromise = tournaments.length
        ? Promise.resolve()
        : store.dispatch(tournamentManagementActions.getMasterTournaments());

    Promise.all([tournamentsPromise]);

    return getAgeLevels();
};

const Contact = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const recaptcha = useRef<ReCAPTCHA>(null);
    const { ageLevels } = useLoaderData() as IAPIResponse & {
        ageLevels: IAgeLevel[];
    };

    const tournaments = useAppSelector(
        state => state.tournamentmanagement.masterTournaments,
    );

    const contactFormSchema = yup.object().shape({
        contactType: yup
            .string()
            .required("What can we help you with?")
            .oneOf(
                ["new", "existing", "underway"],
                "What can we help you with?",
            ),
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        email: yup
            .string()
            .required("Email is required")
            .email("Email is invalid"),
        confirmEmail: yup
            .string()
            .required("Email is required")
            .email("Email is invalid")
            .oneOf([yup.ref("email")], "Email does not match"),
        teamName: yup.string().required("Team Name is required"),
        comment: yup.string().required("Comment/Question is required"),
        ageLevel: yup
            .object()
            .shape({
                value: yup.number().required(),
                label: yup
                    .string()
                    .required("Please select the tournament age level"),
            })
            .required(),
        tournament: yup
            .object()
            .shape({
                value: yup.number().required(),
                label: yup.string().required("Please select the tournament"),
            })
            .required(),
        recaptcha: yup.string().nullable(),
    });

    const verifyCaptcha = (): boolean => {
        const recaptchaValue = recaptcha.current?.getValue();
        if (!recaptchaValue) {
            setError("recaptcha", {
                type: "custom",
                message: "Please verify the reCAPTCHA!",
            });
            return false;
        } else {
            clearErrors("recaptcha");
            return true;
        }
    };

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        setValue,
        control,
        watch,
        formState: { errors },
    } = useForm<IContactFormData>({
        mode: "onChange",
        resolver: yupResolver(contactFormSchema),
    });

    const onSubmit: SubmitHandler<IContactFormData> = async data => {
        if (verifyCaptcha()) {
            await submitContact(data)
                .then(response => {
                    dispatch(
                        alertActions.success({
                            text: "Thank you for reaching out to us. We will respond as soon as possible.",
                        }),
                    );
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem sending your contact request.`,
                            text: "If the problem continues, please try emailing us directly.",
                            dismissible: true,
                        }),
                    );
                });
        }
    };

    const onError: SubmitErrorHandler<IContactFormData> = (): void => {
        verifyCaptcha();
    };

    const onChangeRecaptcha = async (token: string | null): Promise<void> => {
        const recaptchaValue = recaptcha.current?.getValue();
        setValue("recaptcha", recaptchaValue);
        verifyCaptcha();
        // TODO: Verify response by sending token to back-end API
    };

    const watchContactType = watch("contactType");

    return (
        <div data-testid="contactContent">
            <Container className="py-5 contact">
                <Form
                    onSubmit={handleSubmit(onSubmit, onError)}
                    id="contactForm"
                >
                    <Row>
                        <Col>
                            <h2>How can we help?</h2>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <h4>Please choose an option below.</h4>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Select
                                {...register("contactType", { required: true })}
                                id="contactType"
                                className="mb-4"
                                defaultValue=""
                                aria-invalid={
                                    errors.contactType ? "true" : "false"
                                }
                            >
                                <option disabled value="">
                                    Select Option
                                </option>
                                <option value="existing">
                                    Already registered but have a question?
                                </option>
                                <option value="new">
                                    Considering joining one of our tournaments?
                                </option>
                                <option value="underway">
                                    Tournament is underway, but have a question?
                                </option>
                            </Form.Select>
                            {errors.contactType &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.contactType.message as string,
                                })}
                        </Col>
                    </Row>
                    {["new", "existing"].includes(watchContactType) && (
                        <>
                            <Row>
                                <Form.Label>Name *</Form.Label>
                                <Form.Group as={Col} controlId="firstName">
                                    <Form.Control
                                        type="text"
                                        {...register("firstName", {
                                            required: "First Name is required",
                                        })}
                                        aria-invalid={
                                            errors.firstName ? "true" : "false"
                                        }
                                    />
                                    <Form.Label>First</Form.Label>
                                    {errors.firstName &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.firstName
                                                .message as string,
                                        })}
                                </Form.Group>
                                <Form.Group as={Col} controlId="lastName">
                                    <Form.Control
                                        type="text"
                                        {...register("lastName", {
                                            required: "Last Name is required",
                                        })}
                                        aria-invalid={
                                            errors.lastName ? "true" : "false"
                                        }
                                    />
                                    <Form.Label>Last</Form.Label>
                                    {errors.lastName &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.lastName
                                                .message as string,
                                        })}
                                </Form.Group>
                            </Row>
                            <Row>
                                <Form.Label>Email *</Form.Label>
                                <Form.Group as={Col} controlId="email">
                                    <Form.Control
                                        type="email"
                                        {...register("email", {
                                            required:
                                                "Email address  is required",
                                        })}
                                        aria-invalid={
                                            errors.email ? "true" : "false"
                                        }
                                    />
                                    <Form.Label>Enter Email</Form.Label>
                                    {errors.email &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.email
                                                .message as string,
                                        })}
                                </Form.Group>
                                <Form.Group as={Col} controlId="confirmEmail">
                                    <Form.Control
                                        type="email"
                                        {...register("confirmEmail", {
                                            required:
                                                "Please confirm your email address",
                                        })}
                                        aria-invalid={
                                            errors.confirmEmail
                                                ? "true"
                                                : "false"
                                        }
                                    />
                                    <Form.Label>Confirm Email</Form.Label>
                                    {errors.confirmEmail &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.confirmEmail
                                                .message as string,
                                        })}
                                </Form.Group>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group controlId="teamName">
                                        <Form.Label>Team Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            {...register("teamName", {
                                                required:
                                                    "Team Name is required",
                                            })}
                                            aria-invalid={
                                                errors.teamName
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        {errors.teamName &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: errors.teamName
                                                    .message as string,
                                            })}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group controlId="ageLevel">
                                        <Form.Label>Age *</Form.Label>
                                        <Controller
                                            name="ageLevel"
                                            control={control}
                                            defaultValue={undefined}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    isSearchable={false}
                                                    defaultValue={undefined}
                                                    isClearable={true}
                                                    required={false}
                                                    options={ageLevels.map(
                                                        a => {
                                                            return {
                                                                value: parseInt(
                                                                    a.id.toString(),
                                                                ),
                                                                label: a.ageLevel,
                                                            };
                                                        },
                                                    )}
                                                    aria-invalid={
                                                        errors.ageLevel
                                                            ? "true"
                                                            : "false"
                                                    }
                                                />
                                            )}
                                        />
                                        {errors.ageLevel &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: errors.ageLevel
                                                    .message as string,
                                            })}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group controlId="tournament">
                                        <Form.Label>Tournament *</Form.Label>
                                        <Controller
                                            name="tournament"
                                            control={control}
                                            defaultValue={undefined}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    isSearchable={false}
                                                    defaultValue={undefined}
                                                    isClearable={true}
                                                    required={false}
                                                    options={tournaments.map(
                                                        t => {
                                                            return {
                                                                value: parseInt(
                                                                    t.id.toString(),
                                                                ),
                                                                label:
                                                                    t.name ||
                                                                    "",
                                                            };
                                                        },
                                                    )}
                                                    aria-invalid={
                                                        errors.ageLevel
                                                            ? "true"
                                                            : "false"
                                                    }
                                                />
                                            )}
                                        />
                                        {errors.ageLevel &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: errors.ageLevel
                                                    .message as string,
                                            })}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Form.Label>Comment *</Form.Label>
                                <Form.Group as={Col} controlId="comment">
                                    <Form.Control
                                        as="textarea"
                                        rows={10}
                                        cols={50}
                                        {...register("comment")}
                                    />
                                </Form.Group>
                            </Row>
                            <Row>
                                <Col xs={3}>
                                    <Form.Label>CAPTCHA</Form.Label>
                                    <ReCAPTCHA
                                        ref={recaptcha}
                                        sitekey={
                                            process.env
                                                .RECAPTCHA_SITE_KEY as string
                                        }
                                        onChange={onChangeRecaptcha}
                                    />
                                    {errors.recaptcha &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.recaptcha
                                                .message as string,
                                        })}
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Button variant="primary" type="submit">
                                        Submit
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    )}
                    {watchContactType === "underway" && (
                        <div>
                            <p className="mt-3 mb-5">
                                <span className="h4">
                                    Tournament is underway, but have a question?
                                </span>
                            </p>
                            <p>
                                <span className="h4">
                                    Please contact the on-site Tournament
                                    Director. If you are unable to do so for any
                                    reason, please contact{" "}
                                    <a href="mailto:help@wisconsinbaseballtournaments.com">
                                        help@wisconsinbaseballtournaments.com
                                    </a>
                                    , but please be aware any reply may be
                                    delayed for several hours.
                                </span>
                            </p>

                            <p className="text-lg">
                                <b>
                                    Please be advised we will only communicate
                                    directly with the head coach. It is not
                                    possible with our staffing to to answer
                                    inquiries from the approx. 15,000+ families
                                    that participate in our tournaments each
                                    year. If you are not the head coach, please
                                    address your question/concern to your head
                                    coach and they can contact us, if they are
                                    unable to answer your question or address
                                    your concern.
                                </b>
                            </p>

                            <p className="text-lg">
                                <b>
                                    If you have questions regarding weather
                                    related delays or cancellations, please
                                    check out the top of our home page for any
                                    weather related notices.
                                </b>
                            </p>
                        </div>
                    )}
                </Form>
            </Container>
        </div>
    );
};

export default Contact;
